import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { actions, ConfigScopesEnum } from 'builder/modules/init'
import { Scope as i18nScopeType } from 'builder/context/I18nContext'
import UserScope from 'builder/components/UserScope'
import { useCareerProfileData } from 'builder/hooks/useCareerProfileData'
import { useCareerProfileCompleteness } from 'builder/hooks/useCareerProfileCompleteness'
import { useCareerInsightsData } from 'builder/hooks/useCareerInsightsData'
import { actions as userActions, selectors as userSelectors } from 'builder/modules/user'
import { useConfig } from 'builder/hooks/useConfig'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useI18n } from 'builder/hooks/useI18n'
import { useIsEditorPage } from 'builder/hooks/useIsEditorPage'

type SmartRouteProps = {
  configScope?: ConfigScopesEnum
  i18nScope?: i18nScopeType
  privateRoute?: boolean
  premiumRoute?: boolean
}

const SmartRoute = (props: SmartRouteProps) => {
  const {
    configScope = ConfigScopesEnum.app,
    i18nScope = 'builder',
    privateRoute,
    premiumRoute,
  } = props
  const config = useConfig(configScope)
  const { isScopeLoaded, isScopeLoading, isEditorScopeLoading, loadScope, isEditorScopeLoaded } =
    useI18n()
  const dispatch = useDispatch()
  const { isEditPage, isGuestResumeEditor } = useIsEditorPage()

  const isUserFetched = useTypedSelector(userSelectors.isFetched)
  const isI18nScopeReady = isScopeLoaded(i18nScope)
  const isI18nScopeLoading = isScopeLoading(i18nScope)
  const isI18nEditorScopeReady = isEditorScopeLoaded(i18nScope)
  const isI18nEditorScopeLoading = isEditorScopeLoading(i18nScope)

  useEffect(() => {
    if (!isUserFetched && privateRoute) {
      dispatch(userActions.fetchUserRequest())
    }
  }, [dispatch, isUserFetched, privateRoute])

  useEffect(() => {
    if (!config) {
      dispatch(actions.fetchConfig({ scope: configScope }))
    }
  }, [config, configScope, dispatch])

  useEffect(() => {
    if (
      (!isI18nScopeReady && !isI18nScopeLoading) ||
      (isEditPage && !isI18nEditorScopeReady && !isI18nEditorScopeLoading)
    ) {
      loadScope(i18nScope)
    }
  }, [
    i18nScope,
    isI18nScopeReady,
    loadScope,
    isI18nScopeLoading,
    isEditPage,
    isI18nEditorScopeReady,
    isI18nEditorScopeLoading,
  ])

  useCareerProfileData(isEditPage)
  useCareerProfileCompleteness(isEditPage)
  useCareerInsightsData(isEditPage)

  if (
    !config ||
    (!isUserFetched && privateRoute) ||
    (!isI18nScopeReady && !isEditPage) ||
    (isGuestResumeEditor && !isI18nEditorScopeReady)
  ) {
    return null
  }

  return privateRoute ? (
    <UserScope premium={premiumRoute}>
      <Outlet />
    </UserScope>
  ) : (
    <>
      <Outlet />
    </>
  )
}

export default SmartRoute
